// 熨烫效果预览渲染器（纯前端 Canvas，不影响图纸生成逻辑）
// 扩展新效果：在 IRONING_EFFECTS 注册表中加一条配置即可
//   beadStyle: 'holed'（半熔有孔）| 'flat'（无孔方豆）
//   overlay: 可选，在豆子平铺后叠加的蒙版（高光 / 闪粉 / 颗粒等）
import { hexToRgb } from './color'

// 颜色变亮（factor > 1）或变暗（factor < 1）
function shade(hex, factor) {
  const { r, g, b } = hexToRgb(hex)
  const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)))
  return `rgb(${clamp(r * factor)},${clamp(g * factor)},${clamp(b * factor)})`
}

// ============ 噪点 / 颗粒生成器（带缓存，只生成一次） ============

// 闪粉噪点贴图：随机散布的亮点（格利特 / 细闪 / 粗闪用）
const speckleCache = new Map()
function getSpeckleNoise({ density = 0.05, minR = 0.6, maxR = 1.6, colorful = false, tile = 256 } = {}) {
  const key = `${density}|${minR}|${maxR}|${colorful}|${tile}`
  if (speckleCache.has(key)) return speckleCache.get(key)

  const size = tile
  const c = document.createElement('canvas')
  c.width = c.height = size
  const ctx = c.getContext('2d')
  const count = Math.floor(density * size * size)
  // 格利特烫的闪粉带轻微彩色（银白为主，少量蓝/粉/金）
  const palette = colorful
    ? ['#ffffff', '#ffffff', '#ffffff', '#dff1ff', '#ffe3f1', '#fff6cf']
    : ['#ffffff']
  for (let i = 0; i < count; i++) {
    const r = minR + Math.random() * (maxR - minR)
    ctx.globalAlpha = 0.45 + Math.random() * 0.55
    ctx.fillStyle = palette[Math.floor(Math.random() * palette.length)]
    ctx.beginPath()
    ctx.arc(Math.random() * size, Math.random() * size, r, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.globalAlpha = 1
  speckleCache.set(key, c)
  return c
}

// 细密颗粒（毛巾烫用）
let grainNoise = null
function getGrainNoise(size = 128) {
  if (grainNoise) return grainNoise
  const c = document.createElement('canvas')
  c.width = c.height = size
  const ctx = c.getContext('2d')
  const img = ctx.createImageData(size, size)
  for (let i = 0; i < img.data.length; i += 4) {
    const v = 190 + Math.random() * 65
    img.data[i] = img.data[i + 1] = img.data[i + 2] = v
    img.data[i + 3] = 255
  }
  ctx.putImageData(img, 0, 0)
  grainNoise = c
  return c
}

// 粗大颗粒（澡巾烫用）：小噪点图放大 3 倍且不平滑，形成块状毛圈肌理
let blockGrain = null
function getBlockGrain() {
  if (blockGrain) return blockGrain
  const small = document.createElement('canvas')
  small.width = small.height = 64
  const sctx = small.getContext('2d')
  const img = sctx.createImageData(64, 64)
  for (let i = 0; i < img.data.length; i += 4) {
    const v = 150 + Math.random() * 105
    img.data[i] = img.data[i + 1] = img.data[i + 2] = v
    img.data[i + 3] = 255
  }
  sctx.putImageData(img, 0, 0)

  blockGrain = document.createElement('canvas')
  blockGrain.width = blockGrain.height = 192
  const bctx = blockGrain.getContext('2d')
  bctx.imageSmoothingEnabled = false
  bctx.drawImage(small, 0, 0, 192, 192)
  return blockGrain
}

// ============ 通用叠加 ============

// 斜向镜面反光（无孔类效果通用），strength 控制高光强度
function specularOverlay(ctx, w, h, strength = 1) {
  ctx.save()
  ctx.globalCompositeOperation = 'overlay'
  const g = ctx.createLinearGradient(0, 0, w, h)
  g.addColorStop(0, `rgba(255,255,255,${0.3 * strength})`)
  g.addColorStop(0.5, 'rgba(255,255,255,0)')
  g.addColorStop(1, `rgba(0,0,0,${0.08 * strength})`)
  ctx.fillStyle = g
  ctx.fillRect(0, 0, w, h)
  ctx.restore()
}

// 平铺贴图叠加
function tileOverlay(ctx, w, h, tile, operation, alpha) {
  ctx.save()
  ctx.globalCompositeOperation = operation
  ctx.globalAlpha = alpha
  ctx.fillStyle = ctx.createPattern(tile, 'repeat')
  ctx.fillRect(0, 0, w, h)
  ctx.restore()
}

// ============ 效果注册表：新增蒙版效果只需在此添加一条 ============

export const IRONING_EFFECTS = [
  { key: 'pattern', label: '图纸', beadStyle: null, overlay: null },
  { key: 'half', label: '半熔', beadStyle: 'holed', overlay: null },
  // 正常烫：平整方豆，边缘有接缝，轻微光泽
  {
    key: 'normal',
    label: '正常烫',
    beadStyle: 'flat',
    overlay: (ctx, w, h) => specularOverlay(ctx, w, h, 0.8),
  },
  // 细闪烫：平整哑光底 + 稀疏细小闪点
  {
    key: 'fine',
    label: '细闪烫',
    beadStyle: 'flat',
    overlay: (ctx, w, h) => {
      specularOverlay(ctx, w, h, 0.6)
      tileOverlay(ctx, w, h, getSpeckleNoise({ density: 0.02, minR: 0.5, maxR: 1 }), 'screen', 0.45)
    },
  },
  // 粗闪烫：平整底 + 较大的闪点，分布中等
  {
    key: 'coarse',
    label: '粗闪烫',
    beadStyle: 'flat',
    overlay: (ctx, w, h) => {
      specularOverlay(ctx, w, h, 0.6)
      // 粗闪：大亮片、极稀疏——约每 4~10 颗豆 2 粒（间距≈5 豆），512 大贴图减少重复感
      tileOverlay(ctx, w, h, getSpeckleNoise({ density: 0.00016, minR: 1.2, maxR: 4, tile: 512 }), 'screen', 0.75)
    },
  },
  // 格利特烫：密集、大小不一、略带彩色的闪粉颗粒
  {
    key: 'glitter',
    label: '格利特烫',
    beadStyle: 'flat',
    overlay: (ctx, w, h) => {
      specularOverlay(ctx, w, h, 0.5)
      tileOverlay(ctx, w, h, getSpeckleNoise({ density: 0.1, minR: 0.6, maxR: 2, colorful: true }), 'screen', 0.6)
    },
  },
  // 毛巾烫：细密毛圈颗粒，哑光柔和
  {
    key: 'towel',
    label: '毛巾烫',
    beadStyle: 'flat',
    overlay: (ctx, w, h) => {
      tileOverlay(ctx, w, h, getGrainNoise(), 'multiply', 0.2)
      tileOverlay(ctx, w, h, getGrainNoise(), 'overlay', 0.14)
    },
  },
  // 澡巾烫：粗大块状毛圈肌理，对比强烈
  {
    key: 'bath',
    label: '澡巾烫',
    beadStyle: 'flat',
    overlay: (ctx, w, h) => {
      tileOverlay(ctx, w, h, getBlockGrain(), 'multiply', 0.35)
      tileOverlay(ctx, w, h, getBlockGrain(), 'overlay', 0.15)
    },
  },
]

// ============ 豆子贴图 ============

// 圆角矩形路径（兼容不支持 ctx.roundRect 的环境）
function roundRectPath(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + rr, y)
  ctx.arcTo(x + w, y, x + w, y + h, rr)
  ctx.arcTo(x + w, y + h, x, y + h, rr)
  ctx.arcTo(x, y + h, x, y, rr)
  ctx.arcTo(x, y, x + w, y, rr)
  ctx.closePath()
}

// 生成单颗豆子贴图（2x 尺寸保证缩放清晰），按颜色缓存复用
// 熨烫后豆子仍是方形：贴图填满整格，无圆形缝隙
function makeBeadSprite(hex, cell, style) {
  const s = cell * 2
  const c = document.createElement('canvas')
  c.width = c.height = s
  const ctx = c.getContext('2d')

  // 半熔：每颗豆子独立光影渐变，突出单颗立体感
  // 无孔类（正常/闪/毛巾/澡巾烫）：纯色平涂，光影由整体蒙版统一叠加，豆间无亮度断层
  if (style === 'holed') {
    const body = ctx.createLinearGradient(0, 0, s, s)
    body.addColorStop(0, shade(hex, 1.22))
    body.addColorStop(0.55, hex)
    body.addColorStop(1, shade(hex, 0.82))
    ctx.fillStyle = body
  } else {
    ctx.fillStyle = hex
  }
  // 半熔保留微小圆角与接缝；无孔类满铺零圆角，豆间无缝隙
  const radius = style === 'holed' ? s * 0.08 : 0
  roundRectPath(ctx, 0, 0, s, s, radius)
  ctx.fill()

  if (style === 'holed') {
    // 接缝暗线（仅半熔）
    ctx.strokeStyle = shade(hex, 0.7)
    ctx.lineWidth = Math.max(1, s * 0.03)
    ctx.stroke()

    // 半熔：中心留孔（方形孔洞，内深外浅）
    const hole = s * 0.34
    const hx = (s - hole) / 2
    const hy = (s - hole) / 2
    const holeGrad = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, hole * 0.8)
    holeGrad.addColorStop(0, shade(hex, 0.42))
    holeGrad.addColorStop(1, shade(hex, 0.75))
    ctx.fillStyle = holeGrad
    roundRectPath(ctx, hx, hy, hole, hole, hole * 0.25)
    ctx.fill()
  }
  return c
}

/**
 * 渲染熨烫效果预览
 * @param {object} pattern { grid, width, height }
 * @param {object} brand 品牌色库
 * @param {number} cell 每格像素
 * @param {string} effectKey IRONING_EFFECTS 中的 key（'pattern' 以外）
 * @returns {HTMLCanvasElement}
 */
export function renderIroningPreview(pattern, brand, cell, effectKey) {
  const effect = IRONING_EFFECTS.find((e) => e.key === effectKey) || IRONING_EFFECTS[0]
  const { grid, width, height } = pattern
  const colorMap = new Map(brand.colors.map((c) => [c.id, c.hex]))

  const canvas = document.createElement('canvas')
  canvas.width = width * cell
  canvas.height = height * cell
  const ctx = canvas.getContext('2d')

  // 米色背景，模拟垫纸/熨烫纸
  ctx.fillStyle = '#f7f2ea'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  // 按颜色缓存豆子贴图，整张图每种颜色只画一次
  const spriteCache = new Map()
  const getSprite = (hex) => {
    if (!spriteCache.has(hex)) {
      spriteCache.set(hex, makeBeadSprite(hex, cell, effect.beadStyle))
    }
    return spriteCache.get(hex)
  }

  ctx.imageSmoothingEnabled = true
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const hex = colorMap.get(grid[y][x]) || '#ffffff'
      ctx.drawImage(getSprite(hex), x * cell, y * cell, cell, cell)
    }
  }

  // 叠加效果专属蒙版（高光 / 闪粉 / 颗粒）
  if (effect.overlay) {
    effect.overlay(ctx, canvas.width, canvas.height)
  }

  return canvas
}
